import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { dashboardAPI } from 'services/api';

const UserRoleDistribution = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    farmers: 0,
    agents: 0,
    admins: 0,
    aicOfficers: 0,
    total: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getAdminDashboard();
      const data = response.data;
      
      setUserData({
        farmers: data.user_stats?.farmers || 0,
        agents: data.user_stats?.agents || 0,
        admins: data.user_stats?.admins || 0,
        aicOfficers: data.user_stats?.aic_officers || 0,
        total: data.total_users || 0
      });
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user role distribution data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const chartData = [
    { id: 0, value: userData.farmers, label: 'Farmers' },
    { id: 1, value: userData.agents, label: 'Agents' },
    { id: 2, value: userData.admins, label: 'Admins' },
    { id: 3, value: userData.aicOfficers, label: 'AIC Officers' }
  ].filter(item => item.value > 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
        User Role Distribution
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4" color="primary">
                {userData.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Farmers
              </Typography>
              <Typography variant="h4" color="success.main">
                {userData.farmers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((userData.farmers / userData.total) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Agents
              </Typography>
              <Typography variant="h4" color="warning.main">
                {userData.agents}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((userData.agents / userData.total) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Admins
              </Typography>
              <Typography variant="h4" color="error.main">
                {userData.admins}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((userData.admins / userData.total) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AIC Officers
              </Typography>
              <Typography variant="h4" color="info.main">
                {userData.aicOfficers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((userData.aicOfficers / userData.total) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pie Chart */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Role Distribution Chart
          </Typography>
          <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {chartData.length > 0 ? (
              <PieChart
                series={[
                  {
                    data: chartData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                  }
                ]}
                height={400}
                width={600}
              />
            ) : (
              <Typography color="text.secondary">No user data available</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserRoleDistribution;
